import React from 'react';
import Icon from '../../../components/AppIcon';

const TechnologyPartners = () => {
  const partners = [
    {
      name: 'Microsoft',
      icon: 'Box',
      description: 'Azure Cloud Solutions & Enterprise Software',
      certifications: ['Gold Partner', 'Cloud Platform', 'Modern Workplace'],
      integrations: ['Azure AD', 'Office 365', 'Dynamics 365', 'Power Platform'],
      color: 'from-blue-500/10 to-blue-600/20',
      textColor: 'text-blue-700',
      gradient: 'bg-gradient-to-br from-blue-50 to-blue-100'
    },
    {
      name: 'Zoho',
      icon: 'Layers',
      description: 'Business Applications & CRM Solutions',
      certifications: ['Premium Partner', 'Implementation Expert', 'Training Certified'],
      integrations: ['Zoho CRM', 'Zoho Books', 'Zoho Projects', 'Zoho Analytics'],
      color: 'from-emerald-500/10 to-emerald-600/20',
      textColor: 'text-emerald-700',
      gradient: 'bg-gradient-to-br from-emerald-50 to-emerald-100'
    },
    {
      name: 'Acronis',
      icon: 'Shield',
      description: 'Cyber Protection & Backup Solutions',
      certifications: ['Elite Partner', 'Cyber Protection', 'Cloud Backup'],
      integrations: ['Acronis Backup', 'Cyber Protect', 'Disaster Recovery', 'Ransomware Protection'],
      color: 'from-purple-500/10 to-purple-600/20',
      textColor: 'text-purple-700',
      gradient: 'bg-gradient-to-br from-purple-50 to-purple-100'
    }
  ];

  return (
    <section className="py-16 md:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 to-secondary/10 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-white/5 bg-grid-pattern opacity-10"></div>
      <div className="absolute top-0 left-1/4 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-purple-500/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 rounded-full mb-6 backdrop-blur-sm border border-white/10 shadow-lg">
            <Icon name="Award" size={18} className="text-white" />
            <span className="text-sm font-semibold text-white tracking-wider">TECHNOLOGY PARTNERSHIPS</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Enterprise-Grade
            </span>{" "}
            Technology Partnerships
          </h2>
          
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            We collaborate with industry leaders to deliver cutting-edge solutions with seamless integration capabilities
          </p>
        </div>
        
        {/* Partners Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {partners?.map((partner, index) => (
            <div 
              key={index} 
              className="group relative bg-gradient-to-br from-gray-800/50 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-white/10 shadow-2xl hover:shadow-blue-500/20 hover:border-blue-500/30 transition-all duration-500 hover:-translate-y-2 overflow-hidden"
            >
              {/* Hover Effect Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Card Content */}
              <div className="relative p-8">
                {/* Icon & Header */}
                <div className={`absolute -top-4 -right-4 w-24 h-24 ${partner.gradient} rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-500`}></div>
                
                <div className="flex items-start gap-4 mb-6">
                  <div className={`relative p-4 ${partner.gradient} rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <Icon name={partner?.icon} size={28} className={partner.textColor} />
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl"></div>
                  </div>
                  
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-1">{partner?.name}</h3>
                    <p className="text-gray-400 text-sm">{partner?.description}</p>
                  </div>
                </div>
                
                {/* Certifications */}
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="BadgeCheck" size={18} className="text-blue-400" />
                    <span className="text-white font-semibold text-sm uppercase tracking-wider">Certifications</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {partner?.certifications?.map((cert, idx) => (
                      <span 
                        key={idx} 
                        className={`px-3 py-1.5 ${partner.gradient} ${partner.textColor} rounded-full text-xs font-semibold border border-white/10 shadow-sm backdrop-blur-sm flex items-center gap-1.5`}
                      >
                        <Icon name="CheckCircle" size={10} />
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Integration Capabilities */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="Zap" size={18} className="text-purple-400" />
                    <span className="text-white font-semibold text-sm uppercase tracking-wider">Integration Capabilities</span>
                  </div>
                  <div className="space-y-3">
                    {partner?.integrations?.map((integration, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-xl border border-white/5 hover:bg-gray-800/50 transition-colors duration-200">
                        <div className={`p-2 ${partner.gradient} rounded-lg`}>
                          <Icon name="Link" size={14} className={partner.textColor} />
                        </div>
                        <span className="text-gray-300 text-sm font-medium">{integration}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Bottom Gradient */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${partner.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
            </div>
          ))}
        </div>
        
        {/* CTA Section */}
        <div className="relative bg-gradient-to-br from-primary/10 to-secondary/10  backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-grid-white/5 bg-grid-pattern opacity-10"></div>
          
          <div className="relative p-8 md:p-10 lg:p-12">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg">
                    <Icon name="MessageSquare" size={22} className="text-white" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    Need Custom Integration Solutions?
                  </h3>
                </div>
                <p className="text-lg text-gray-300 leading-relaxed max-w-2xl">
                  Our team specializes in seamless integration with virtually any enterprise platform. 
                  Let's discuss your specific requirements and technology ecosystem.
                </p>
              </div>
              
              <button className="group relative bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-xl hover:shadow-blue-500/30 hover:shadow-2xl transition-all duration-300 overflow-hidden flex items-center gap-3 whitespace-nowrap min-w-[220px] justify-center">
                {/* Button Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <span className="relative z-10 flex items-center gap-2">
                  <Icon name="MessageCircle" size={20} className="group-hover:scale-110 transition-transform duration-300" />
                  Discuss Integration
                </span>
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-500 blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500"></div>
              </button>
            </div>
            
            {/* Additional Info */}
            <div className="mt-10 pt-8 border-t border-white/10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Icon name="Clock" size={18} className="text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Implementation Time</p>
                    <p className="text-white font-semibold">2-4 Weeks</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Icon name="ShieldCheck" size={18} className="text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Security Compliance</p>
                    <p className="text-white font-semibold">Enterprise Grade</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500/10 rounded-lg">
                    <Icon name="Headphones" size={18} className="text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Support Included</p>
                    <p className="text-white font-semibold">24/7 Premium Support</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Corner Accents */}
          <div className="absolute top-0 left-0 w-20 h-20 border-t border-l border-blue-500/30 rounded-tl-2xl"></div>
          <div className="absolute bottom-0 right-0 w-20 h-20 border-b border-r border-purple-500/30 rounded-br-2xl"></div>
        </div>
      </div>
      
      {/* Add custom CSS for grid pattern */}
      <style jsx>{`
        .bg-grid-pattern {
          background-image: linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px);
          background-size: 40px 40px;
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .group:hover .floating-icon {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default TechnologyPartners;